import {TournamentViewModel2} from "./TournamentScoreViewModel2";

export class TournamentManager {
  dates: number[] = [];
  viewModels: TournamentViewModel2[];
  private readonly key: string;

  sortDatesDesc: boolean = false;

  constructor(viewModels: TournamentViewModel2[], key = "sortAscAverage", sortDatesDesc = false) {
    this.viewModels = viewModels;
    this.sortDatesDesc = sortDatesDesc;
    this.updateDates();
    this.key = key;
    this.defaultSortFunction();
  }

  addNewDate(date: number) {
    this.viewModels.forEach(vm => vm.addScore(date, 0));
    this.dates.push(date);

    if(this.sortDatesDesc) {
      this.dates.sort((a, b) => b - a);
    } else {
      this.dates.sort((a, b) => a - b);
    }

    this.updateScores();
  }

  addNewPlayer(): void {
    const newPlayer = new TournamentViewModel2("Nouveau", {});
    this.dates.forEach(date => newPlayer.addScore(date, 0));
    this.viewModels.push(newPlayer);
  }

  deleteDate(date: number) {
    this.viewModels.forEach(vm => vm.removeScore(date));
    this.updateDates();
    this.updateScores();
  }

  deleteRow(row: TournamentViewModel2): void {
    const index = this.viewModels.indexOf(row);
    if (index > -1) this.viewModels.splice(index, 1);
  }

  updateAverages(): void {
    this.viewModels.forEach(vm => vm.calculateAverageScore());
  }

  updateTotals(): void {
    this.viewModels.forEach(vm => vm.calculateTotalScores());
  }

  update15BestAverage(): void {
    this.viewModels.forEach(vm => vm.calculate15BestAverage());
  }

  updateParticipationCount(): void {
    this.viewModels.forEach(vm => vm.calculateParticipationCount());
  }

  updateScores() {
    this.updateAverages();
    this.updateTotals();
    this.update15BestAverage();
    this.updateParticipationCount();
  }

  updateDates(): void {
    console.log('sortDatesDesc', this.sortDatesDesc);


    const allDates = new Set<number>();
    this.viewModels.forEach(viewModel => {
      Object.keys(viewModel.scoresByDate).forEach(date => allDates.add(Number(date)));
    });

    if(this.sortDatesDesc) {
      this.dates = Array.from(allDates).sort((a, b) => b - a);
    } else {
      this.dates = Array.from(allDates).sort((a, b) => a - b);
    }
  }

  exportToJSON(): string {
    return JSON.stringify(this.viewModels);
  }

  static fromJson(json: string, defaultSortFunction: SortFunctionKey, sortDatesDesc = false): TournamentManager {
    let data;
    try {
      data = JSON.parse(json);
    } catch (error) {
      console.error("Error parsing JSON: " + error);
      return new TournamentManager([], defaultSortFunction, sortDatesDesc);
    }

    if (!Array.isArray(data)) {
      console.error("JSON data is not an array");
      return new TournamentManager([], defaultSortFunction, sortDatesDesc);
    }

    const viewModels = data.map((obj: any) => new TournamentViewModel2(obj.playerName, obj.scoresByDate));
    return new TournamentManager(viewModels, defaultSortFunction, sortDatesDesc);
  }


  sortDescAverage() {
    this.updateScores();

    this.viewModels.sort((a, b) => {
      let averageA = a.averageScore || 0;
      let averageB = b.averageScore || 0;
      return averageB - averageA;
    });
  }

  sortDescTotal() {
    this.updateScores();

    this.viewModels.sort((a, b) => {
      let totalA = a.totalScore || 0;
      let totalB = b.totalScore || 0;
      return totalA - totalB;
    });
  }

  sortAverage15Best() {
    this.updateScores();

    // Sort by average 15 best, then by average
    this.viewModels.sort((a, b) => {
      let average15BestA = a.average15Best || 0;
      let average15BestB   = b.average15Best || 0;

      // Si les deux joueurs ont au moins 15 résultats, on trie par moyenne des 15 meilleures parties
      if (Object.keys(a.scoresByDate).length >= 15 && Object.keys(b.scoresByDate).length >= 15) {
        return average15BestB - average15BestA;
      }

      // Sinon, on trie les joueurs qui n'ont pas encore 15 résultats par moyenne classique
      else if (Object.keys(a.scoresByDate).length < 15 && Object.keys(b.scoresByDate).length < 15) {
        let averageScoreA = a.averageScore || 0;
        let averageScoreB = b.averageScore || 0;
        return averageScoreB - averageScoreA;
      }

      // Si un joueur a au moins 15 résultats et l'autre non, on met le premier en premier
      else {
        return Object.keys(a.scoresByDate).length >= 15 ? -1 : 1;
      }
    });
  }

  defaultSortFunction() {
    this.updateScores();
    switch (this.key) {
      case SortFunctionKey.SortAscAverage:
        this.sortDescAverage();
        break;
      case SortFunctionKey.SortAscTotal:
        this.sortDescTotal();
        break;
      case SortFunctionKey.SortAverage15Best:
        this.sortAverage15Best();
        break;
      default:
        console.error(`Sort function ${this.key} not found`);
    }
  }

  setScoreForDate(date: number, row: TournamentViewModel2, score: number) {
    row.addScore(date, score);
    this.updateScores();
    this.defaultSortFunction();
  }

  getPlayerNames(): string[] {
    return this.viewModels.map(vm => vm.playerName);
  }
}

export enum SortFunctionKey {
  SortAscAverage = 'sortAscAverage',
  SortAscTotal = 'sortAscTotal',
  SortAverage15Best = 'sortAverage15Best'
}
